import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  History, Calendar, User, Filter, ChevronLeft, ChevronRight,
  Activity, FolderTree, GitBranch, FileText, Loader2, AlertCircle,
  Plus, Pencil, Trash2, Link, RefreshCw
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { format } from 'date-fns';

const HistoryPage = () => {
  const { activeSchedule } = useProject();
  const { user } = useAuth();
  
  // Alias for consistency
  const selectedSchedule = activeSchedule;
  
  // State
  const [history, setHistory] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  
  // Users for filter
  const [historyUsers, setHistoryUsers] = useState([]);
  
  // Load history
  const loadHistory = useCallback(async () => {
    if (!selectedSchedule?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url = `/history/schedule/${selectedSchedule.id}?page=${page}&page_size=${pageSize}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (entityFilter) url += `&entity_type=${entityFilter}`;
      if (userFilter) url += `&user_id=${userFilter}`;
      
      const response = await api.get(url);
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [selectedSchedule?.id, page, pageSize, actionFilter, entityFilter, userFilter]);
  
  // Load users for filter
  const loadUsers = useCallback(async () => {
    if (!selectedSchedule?.id) return;
    
    try {
      const response = await api.get(`/history/users?schedule_id=${selectedSchedule.id}`);
      setHistoryUsers(response.data);
    } catch (err) {
      console.error('Failed to load history users:', err);
    }
  }, [selectedSchedule?.id]);
  
  useEffect(() => {
    loadHistory();
    loadUsers();
  }, [loadHistory, loadUsers]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [actionFilter, entityFilter, userFilter]);
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
    } catch {
      return timestamp;
    }
  };
  
  // Get action icon
  const getActionIcon = (action) => {
    if (action.includes('created')) return <Plus className="h-3.5 w-3.5 text-green-500" />;
    if (action.includes('updated')) return <Pencil className="h-3.5 w-3.5 text-blue-500" />;
    if (action.includes('deleted')) return <Trash2 className="h-3.5 w-3.5 text-red-500" />;
    return <Activity className="h-3.5 w-3.5 text-slate-400" />;
  };
  
  // Get entity icon
  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'activity':
        return <Activity className="h-3.5 w-3.5" />;
      case 'wbs':
        return <FolderTree className="h-3.5 w-3.5" />;
      case 'relationship':
        return <Link className="h-3.5 w-3.5" />;
      case 'baseline':
        return <GitBranch className="h-3.5 w-3.5" />;
      case 'schedule':
        return <Calendar className="h-3.5 w-3.5" />;
      default:
        return <FileText className="h-3.5 w-3.5" />;
    }
  };
  
  // Get action badge color
  const getActionBadge = (action) => {
    if (action.includes('created')) {
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Created</Badge>;
    }
    if (action.includes('updated')) {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Updated</Badge>;
    }
    if (action.includes('deleted')) {
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Deleted</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{action}</Badge>;
  };
  
  // Format action for display
  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Format entity type for display
  const formatEntityType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };
  
  // Format change details
  const formatDetails = (entry) => {
    const parts = [];
    
    if (entry.before_value && entry.after_value) {
      // Show what changed
      Object.keys(entry.after_value).forEach(key => {
        if (entry.before_value[key] !== entry.after_value[key]) {
          parts.push(`${key}: ${entry.before_value[key]} → ${entry.after_value[key]}`);
        }
      });
    } else if (entry.after_value) {
      // Created - show new values
      Object.entries(entry.after_value).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          parts.push(`${key}: ${value}`);
        }
      });
    } else if (entry.before_value) {
      // Deleted - show old values
      Object.entries(entry.before_value).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          parts.push(`${key}: ${value}`);
        }
      });
    }
    
    if (entry.details) {
      Object.entries(entry.details).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          parts.push(`${key}: ${value}`);
        }
      });
    }
    
    return parts.length > 0 ? parts.join(', ') : '—';
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(history.total / pageSize);
  
  // No schedule selected
  if (!selectedSchedule) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="history-page">
        <History className="h-12 w-12 text-slate-300 mb-4" />
        <h2 className="text-lg font-medium text-slate-900 mb-2">No Schedule Selected</h2>
        <p className="text-sm text-slate-500">Select a project and schedule to view history</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            History & Audit Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track all changes made to {selectedSchedule.name}
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => { loadHistory(); loadUsers(); }}
          disabled={loading}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <div className="w-full sm:w-auto sm:min-w-[150px]">
              <Select value={entityFilter || "all"} onValueChange={(v) => setEntityFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="activity">Activity</SelectItem>
                  <SelectItem value="wbs">WBS</SelectItem>
                  <SelectItem value="relationship">Relationship</SelectItem>
                  <SelectItem value="baseline">Baseline</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-full sm:w-auto sm:min-w-[150px]">
              <Select value={actionFilter || "all"} onValueChange={(v) => setActionFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="activity_created">Activity Created</SelectItem>
                  <SelectItem value="activity_updated">Activity Updated</SelectItem>
                  <SelectItem value="activity_deleted">Activity Deleted</SelectItem>
                  <SelectItem value="wbs_created">WBS Created</SelectItem>
                  <SelectItem value="wbs_updated">WBS Updated</SelectItem>
                  <SelectItem value="wbs_deleted">WBS Deleted</SelectItem>
                  <SelectItem value="relationship_created">Relationship Created</SelectItem>
                  <SelectItem value="relationship_deleted">Relationship Deleted</SelectItem>
                  <SelectItem value="baseline_created">Baseline Created</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-full sm:w-auto sm:min-w-[150px]">
              <Select value={userFilter || "all"} onValueChange={(v) => setUserFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {historyUsers.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.user_name} ({u.change_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(actionFilter || entityFilter || userFilter) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setActionFilter('');
                  setEntityFilter('');
                  setUserFilter('');
                }}
              >
                Clear Filters
              </Button>
            )}
            
            <div className="ml-auto">
              <Badge variant="outline" className="text-[10px]">
                {history.total} entries
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-slate-900">
            Change History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-44">
                  Timestamp
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-32">
                  User
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">
                  Entity
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">
                  Action
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Entity Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Details
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : history.entries.length > 0 ? (
                history.entries.map((entry) => (
                  <TableRow key={entry.id} className="hover:bg-slate-50/50">
                    <TableCell className="text-xs text-slate-500 tabular-nums">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        {formatTimestamp(entry.timestamp)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <User className="h-3 w-3 text-slate-400" />
                        <span className="text-sm text-slate-700">{entry.user_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {getEntityIcon(entry.entity_type)}
                        <span className="text-sm text-slate-600">
                          {formatEntityType(entry.entity_type)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getActionBadge(entry.action)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-900 font-medium">
                        {entry.entity_name || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-500 truncate max-w-xs block">
                        {formatDetails(entry)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="flex flex-col items-center">
                      <History className="h-8 w-8 text-slate-300 mb-2" />
                      <p className="text-sm text-slate-500">No history entries found</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Changes to this schedule will appear here
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, history.total)} of {history.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoryPage;
